import Layout from "layout";
import React from "react";
import styled from "styled-components";
import { media } from "@/theme/breakpoints";

const PrivacyContainer = styled.div`
  max-width: 800px;
  width: 100%;
  margin: 50px auto;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;

  ${media.downMobile`
    margin: 24px auto;
    padding: 16px;
  `}
`;

const Title = styled.h1`
  text-align: center;
  color: #333;
  margin-bottom: 8px;
`;

const Effective = styled.p`
  text-align: center;
  font-size: 14px;
  color: #777;
  margin-bottom: 20px;
`;

const Subtitle = styled.h2`
  font-size: 18px;
  color: #444;
  margin-top: 28px;
`;

const Content = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #555;
`;

const List = styled.ul`
  margin-top: 10px;
  padding-left: 20px;
`;

const ListItem = styled.li`
  margin-bottom: 10px;
  color: #555;
  list-style-type: disc;
`;

const PrivacyPolicy = () => {
  return (
    <Layout>
      <PrivacyContainer>
        <Title>Privacy Policy</Title>
        <Effective>Last updated: August 24, 2026</Effective>

        <Content>
          This Privacy Policy describes how Ecom Dropship ("Ecom Dropship,"
          "we," "us," or "our") collects, uses, and discloses personal
          information when a merchant installs or uses our application (the
          "App") in connection with their Shopify-powered store (the
          "Service"). By installing or using the App, you agree to the
          collection and use of information in accordance with this policy.
        </Content>

        <Subtitle>Information We Collect</Subtitle>
        <Content>
          To provide the Service, we access certain information from Shopify
          about your store, and we may also collect information directly
          from you and automatically through your use of the App.
        </Content>
        <List>
          <ListItem>
            Store and account information accessed through the Shopify API,
            including your store name, domain, contact and billing details,
            and staff account information needed to operate the App.
          </ListItem>
          <ListItem>
            Product and inventory data, including product listings,
            variants, pricing, and stock levels, used to sync and fulfill
            items through our supplier network.
          </ListItem>
          <ListItem>
            Order data, including order details, line items, shipping
            addresses, and return/refund requests, used to route orders to
            suppliers and fulfill them.
          </ListItem>
          <ListItem>
            Customer information tied to orders, such as customer name,
            email, phone number, and shipping address, used only to fulfill
            and ship orders placed through your store.
          </ListItem>
          <ListItem>
            Information you provide directly, such as your name, email
            address, and business details when you register for an account,
            apply to become a supplier, or contact our support team.
          </ListItem>
          <ListItem>
            Payment information processed through our payment providers
            (Stripe and PayPal) to bill for subscriptions or process
            transactions. We do not collect or store full payment card
            numbers on our own servers.
          </ListItem>
          <ListItem>
            Usage and device data collected automatically, such as IP
            address, browser type, device identifiers, time zone, and pages
            visited, collected using cookies, log files, and similar
            tracking technologies.
          </ListItem>
        </List>

        <Subtitle>How We Use Your Information</Subtitle>
        <List>
          <ListItem>
            To provide, operate, and maintain the App and the Service,
            including syncing products, processing orders, and coordinating
            fulfillment with suppliers.
          </ListItem>
          <ListItem>
            To process payments and manage subscriptions through our payment
            processors.
          </ListItem>
          <ListItem>
            To communicate with you about your account, orders, and support
            requests, and to send important notices about the Service.
          </ListItem>
          <ListItem>
            To monitor and improve the App's performance, security, and
            user experience.
          </ListItem>
          <ListItem>
            To detect, investigate, and prevent fraud, abuse, and security
            incidents.
          </ListItem>
          <ListItem>
            To comply with legal obligations and enforce our Terms of
            Service.
          </ListItem>
        </List>

        <Subtitle>How We Share Your Information</Subtitle>
        <Content>
          We do not sell personal information. We share information only as
          necessary to operate the Service:
        </Content>
        <List>
          <ListItem>
            With suppliers, to fulfill and ship the orders you route through
            the App.
          </ListItem>
          <ListItem>
            With service providers who help us operate the App, including
            payment processors (Stripe, PayPal), hosting and infrastructure
            providers, and, where applicable, marketplace integrations such
            as eBay.
          </ListItem>
          <ListItem>
            With Shopify, as required for the App to function within the
            Shopify platform.
          </ListItem>
          <ListItem>
            When required to comply with a legal obligation, protect our
            rights, or respond to lawful requests from public authorities.
          </ListItem>
          <ListItem>
            In connection with a merger, acquisition, or sale of assets, in
            which case we will notify you before information becomes subject
            to a different privacy policy.
          </ListItem>
        </List>

        <Subtitle>Cookies and Tracking Technologies</Subtitle>
        <Content>
          We use cookies, log files, and similar tracking technologies to
          operate our website, keep you signed in, and understand how the
          Service is used. You can control cookies through your browser
          settings; disabling cookies may affect certain features of the
          Service.
        </Content>

        <Subtitle>Data Retention</Subtitle>
        <Content>
          We retain merchant and order information for as long as your
          account is active or as needed to provide the Service, comply with
          our legal obligations, resolve disputes, and enforce our
          agreements. When information is no longer needed, we delete or
          anonymize it.
        </Content>

        <Subtitle>Data Security</Subtitle>
        <Content>
          We implement reasonable administrative, technical, and physical
          safeguards designed to protect personal information from
          unauthorized access, disclosure, alteration, or destruction.
          However, no method of transmission or storage is completely
          secure, and we cannot guarantee absolute security.
        </Content>

        <Subtitle>Your Rights</Subtitle>
        <Content>
          Depending on where you are located, you may have rights regarding
          your personal information, such as the right to access, correct,
          delete, or restrict our use of your information, and the right to
          data portability. If you are located in the European Economic
          Area or United Kingdom, these rights arise under the GDPR. If you
          are a California resident, you may have additional rights under
          the CCPA. To exercise any of these rights, contact us using the
          details below.
        </Content>

        <Subtitle>International Data Transfers</Subtitle>
        <Content>
          Your information may be transferred to, stored, and processed in
          countries other than the one in which you reside. Where this
          occurs, we take steps to ensure your information receives an
          adequate level of protection wherever it is processed.
        </Content>

        <Subtitle>Children's Privacy</Subtitle>
        <Content>
          The App and Service are not directed to individuals under the age
          of 16, and we do not knowingly collect personal information from
          children.
        </Content>

        <Subtitle>Changes to This Privacy Policy</Subtitle>
        <Content>
          We may update this Privacy Policy from time to time. If we make
          material changes, we will notify you by updating the "Last
          updated" date above and, where appropriate, through additional
          notice within the App.
        </Content>

        <Subtitle>Contact Us</Subtitle>
        <Content>
          If you have questions about this Privacy Policy or how we handle
          your information, please contact us at{" "}
          <a href="mailto:privacy@ecomdropship.ai">privacy@ecomdropship.ai</a>{" "}
          or through the Help & Support option on our website.
        </Content>
      </PrivacyContainer>
    </Layout>
  );
};

export default PrivacyPolicy;
